/**
 * Swatch Tooltip Manager
 * Adds tooltips to product variant swatches that have images but won't trigger
 * the customized product model display
 */

class SwatchTooltipManager {
  constructor() {
    this.swatchData = {};
    this.initialized = false;
  }

  /**
   * Initialize the tooltip manager
   * Reads configuration from customized-product-model element if present
   */
  init() {
    if (this.initialized) return;
    
    // Find the customized-product-model element to get its configuration
    const customizedModel = document.querySelector('customized-product-model');
    
    if (customizedModel) {
      try {
        // Parse swatch data
        const swatchDataRaw = customizedModel.dataset.swatchData;
        if (swatchDataRaw) {
          const parsed = JSON.parse(swatchDataRaw);
          // Normalize keys to lowercase for case-insensitive comparison
          this.swatchData = {};
          for (const [key, value] of Object.entries(parsed)) {
            this.swatchData[key.toLowerCase()] = value;
          }
        }
      } catch (e) {
        console.error('SwatchTooltipManager: Failed to parse configuration', e);
      }
    }

    this.initialized = true;
    this.processSwatches();
  }

  /**
   * Check if a value would trigger the customized product model
   * @param {string} value - The option value to check
   * @returns {boolean}
   */
  wouldTriggerCustomizedModel(value) {
    if (!value) return false;

    const normalizedValue = value.trim().toLowerCase();
    
    // Check if value has swatch data with image URL
    const swatchInfo = this.swatchData[normalizedValue];
    return swatchInfo && swatchInfo.imageUrl;
  }

  /**
   * Create tooltip element
   * @param {string} optionName - Name of the option
   * @param {string} optionValue - Value of the option
   * @param {string} imageUrl - URL of the swatch image
   * @param {string} subtitle - Optional subtitle for the option
   * @returns {HTMLElement}
   */
  createTooltip(optionName, optionValue, imageUrl, subtitle = '') {
    const tooltip = document.createElement('div');
    tooltip.className = 'swatch-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    
    // Use larger image for tooltip (600px)
    const largeImageUrl = imageUrl.includes('?') 
      ? imageUrl.replace(/width=\d+/, 'width=600')
      : `${imageUrl}?width=600`;
    
    const subtitleHtml = subtitle 
      ? `<div class="swatch-tooltip__subtitle">${subtitle}</div>` 
      : '';
    
    tooltip.innerHTML = `
      <img 
        src="${largeImageUrl}" 
        alt="${optionValue}" 
        class="swatch-tooltip__image"
        loading="lazy"
      >
      <div class="swatch-tooltip__name">${optionValue}</div>
      ${subtitleHtml}
    `;
    
    return tooltip;
  }

  /**
   * Process all swatches on the page and add tooltips where appropriate
   */
  processSwatches() {
    // Find all swatch inputs in variant-selects elements (product pages)
    const variantSelects = document.querySelectorAll('variant-selects');
    
    variantSelects.forEach(variantSelectsElement => {
      const swatchInputs = variantSelectsElement.querySelectorAll('.swatch-input__input');
      
      swatchInputs.forEach(input => {
        const value = input.value;
        const label = input.nextElementSibling;
        
        if (!label || !label.classList.contains('swatch-input__label')) return;
        
        // Find the swatch element
        const swatchElement = label.querySelector('.swatch');
        if (!swatchElement) return;
        
        // Check if swatch has an image background
        const swatchBackground = getComputedStyle(swatchElement).getPropertyValue('--swatch--background');
        const hasImage = swatchBackground && swatchBackground.includes('url(');
        
        if (!hasImage) return;
        
        // Check if this value would trigger the customized product model
        // if (this.wouldTriggerCustomizedModel(value)) return;
        
        // Extract image URL from background style
        const urlMatch = swatchBackground.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (!urlMatch) return;
        
        const imageUrl = urlMatch[1];
        
        // Get option name from the fieldset legend or label
        const fieldset = input.closest('fieldset');
        let optionName = '';
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            // Extract text before the colon or selected value
            const legendText = legend.textContent.trim();
            optionName = legendText.split(':')[0].trim();
          }
        }
        
        // Check if tooltip already exists
        const wrapper = label.closest('.swatch-input-wrapper');
        if (!wrapper || wrapper.querySelector('.swatch-tooltip')) return;
        
        // Get subtitle from data attribute or swatch data
        const subtitle = wrapper.dataset.subtitle || 
          (this.swatchData[value.trim().toLowerCase()]?.subtitle) || '';
        
        // Create and add tooltip
        const tooltip = this.createTooltip(optionName, value, imageUrl, subtitle);
        wrapper.appendChild(tooltip);
      });
    });

    // Process facet swatches (collection/search pages) if they exist
    this.processFacetSwatches();
  }

  /**
   * Process swatch filters in facets (collection/search pages)
   */
  processFacetSwatches() {
    const facetSwatches = document.querySelectorAll('.swatch-input-wrapper .swatch-input__input');
    
    facetSwatches.forEach(input => {
      // Skip if already processed or in variant-selects
      if (input.closest('variant-selects')) return;
      
      const label = input.nextElementSibling;
      
      if (!label || !label.classList.contains('swatch-input__label')) return;
      
      const swatchElement = label.querySelector('.swatch');
      if (!swatchElement) return;
      
      // Check if swatch has an image background
      const swatchBackground = getComputedStyle(swatchElement).getPropertyValue('--swatch--background');
      const hasImage = swatchBackground && swatchBackground.includes('url(');
      
      if (!hasImage) return;
      
      // For facets, we always show tooltip if there's an image
      // (since facets are not product-specific, we don't check against enabled values)
      
      // Extract image URL from background style
      const urlMatch = swatchBackground.match(/url\(['"]?([^'"]+)['"]?\)/);
      if (!urlMatch) return;
      
      const imageUrl = urlMatch[1];
      
      // Get filter name from parent structure
      const details = input.closest('details');
      let filterName = '';
      if (details) {
        const summary = details.querySelector('summary .facets__summary-label');
        if (summary) {
          filterName = summary.textContent.trim();
        }
      }
      
      // Check if tooltip already exists
      const wrapper = label.closest('.swatch-input-wrapper');
      if (!wrapper || wrapper.querySelector('.swatch-tooltip')) return;
      
      // Get the human-readable label from data attribute (falls back to input value)
      const displayLabel = wrapper.dataset.label || input.value;
      
      // Get subtitle from data attribute
      const subtitle = wrapper.dataset.subtitle || '';
      
      // Create and add tooltip
      const tooltip = this.createTooltip(filterName, displayLabel, imageUrl, subtitle);
      wrapper.appendChild(tooltip);
    });
  }

  /**
   * Re-process swatches (useful after dynamic content updates)
   */
  refresh() {
    // Remove existing tooltips
    document.querySelectorAll('.swatch-tooltip').forEach(tooltip => {
      tooltip.remove();
    });
    
    // Re-process
    this.processSwatches();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const manager = new SwatchTooltipManager();
    manager.init();
    
    // Make available globally for re-initialization if needed
    window.swatchTooltipManager = manager;
  });
} else {
  const manager = new SwatchTooltipManager();
  manager.init();
  window.swatchTooltipManager = manager;
}

// Re-process swatches when variant selections change (for dynamically loaded content)
if (typeof subscribe !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, () => {
    if (window.swatchTooltipManager) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        window.swatchTooltipManager.refresh();
      }, 100);
    }
  });
}

